import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdwCheckboxComponent } from '@cdw-ae/common-ui-components-checkbox';
import { has } from 'lodash';
import { take } from 'rxjs';

import { FlattenedTreeNode, TreeNode } from '../models/cdw-tree.models';

type TreeNodeState = TreeNode & {
  parent?: TreeNode;
  selected: boolean;
  indeterminate?: boolean;
};

@Component({
  selector: 'lib-cdw-tree',
  templateUrl: './cdw-tree.component.html',
  styleUrls: ['./cdw-tree.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CdwTreeComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CdwTreeComponent
  implements OnChanges, OnInit, AfterViewInit, AfterViewChecked, ControlValueAccessor
{
  @Input() selectable = false;

  @Input() treeHeight = '500px';

  @Input() filterValue!: string;

  @Input() multiSelect = false;

  @Input() progressiveParentSelect = false;

  @Input() scrollToValue: unknown;

  @ViewChild('treeNodesVirtualScroll') viewport!: CdkVirtualScrollViewport;

  @ViewChildren('treeCheckbox') checkboxes!: QueryList<CdwCheckboxComponent>;

  @Input() dataSource!: TreeNode[] | undefined;

  @Output() selectedNodeChanged = new EventEmitter<string | string[]>();

  @Output() renderedNodeCountChanged = new EventEmitter<number>();

  selectedNode!: FlattenedTreeNode | undefined;

  selectedNodes: string[] = [];

  renderedNodes!: FlattenedTreeNode[] | undefined;

  allNodes!: FlattenedTreeNode[] | undefined;

  filteredNodes!: FlattenedTreeNode[] | undefined;

  nodeMap: Map<string | number, TreeNodeState> = new Map();

  // Stores value from writeValue if called before nodeMap is ready
  private pendingValue: unknown = undefined;

  constructor(private changeDetector: ChangeDetectorRef) {}

  private static buildNodeMap(
    tree: TreeNode[]
  ): Map<string | number, TreeNodeState> {
    const map: Map<string | number, TreeNodeState> = new Map();

    function traverse(node: TreeNode, parent?: TreeNode) {
      map.set(node.value as string, {
        ...node,
        parent,
        selected: false,
        indeterminate: false,
      });

      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => traverse(child, node));
      }
    }

    tree.forEach((rootNode) => traverse(rootNode));
    return map;
  }

  ngOnInit(): void {
    if (Array.isArray(this.dataSource)) {
      if (this.dataSource.length > 0) {
        this.allNodes = this.flattenTree(this.dataSource);
        this.filteredNodes = [...this.allNodes];
        this.setRenderedNodes(this.getInitiallyVisibleNodes(this.allNodes));
        this.nodeMap = CdwTreeComponent.buildNodeMap(this.dataSource);

        // Apply any pending value that was set before nodeMap was ready
        if (this.pendingValue !== undefined) {
          this.writeValue(this.pendingValue);
        }
      } else {
        // Initialize empty structures for empty array (not undefined)
        this.allNodes = [];
        this.filteredNodes = [];
        this.setRenderedNodes([]);
        this.nodeMap = new Map();
      }
    }
    // If dataSource is undefined, leave properties undefined
  }

  ngAfterViewInit(): void {
    if (this.scrollToValue != null && this.renderedNodes?.length) {
      const index = this.renderedNodes.findIndex(
        (n) => n.value === this.scrollToValue
      );
      if (index > 0) {
        const scrollIndex = Math.max(0, index - 3);
        setTimeout(() => this.viewport?.scrollToIndex(scrollIndex));
      }
    }
  }

  ngAfterViewChecked(): void {
    this.checkboxes.changes
      .pipe(take(1))
      .subscribe((checkboxes: QueryList<CdwCheckboxComponent>): void => {
        checkboxes.forEach((checkbox: CdwCheckboxComponent) => {
          const checkboxReference: CdwCheckboxComponent = checkbox;
          const nodeValue = (checkboxReference.value as FlattenedTreeNode)
            .value as string;
          const nodeMapNode = this.nodeMap.get(nodeValue);

          if (this.progressiveParentSelect) {
            const flatNode = checkboxReference.value as FlattenedTreeNode;
            checkboxReference.checked = this.getNodeChecked(flatNode);
            checkboxReference.indeterminate = this.getNodeIndeterminate(flatNode);
          } else if (this.multiSelect) {
            checkboxReference.checked = !!nodeMapNode?.selected;
            checkboxReference.indeterminate = !!nodeMapNode?.indeterminate;
          } else {
            checkboxReference.checked = nodeValue === this.selectedNode?.value;
          }
        });
        this.changeDetector.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle dataSource changes
    // biome-ignore lint: TODO: Fix this literal keys
    if (has(changes, 'dataSource') && changes['dataSource'].currentValue) {
      // biome-ignore lint: TODO: Fix this suggestion literal keys
      const newDataSource = changes['dataSource'].currentValue;

      // Validate that newDataSource is actually an array before proceeding
      if (Array.isArray(newDataSource) && newDataSource.length > 0) {
        this.dataSource = newDataSource;
        this.allNodes = this.flattenTree(newDataSource);
        this.filteredNodes = [...this.allNodes];
        this.setRenderedNodes(this.getInitiallyVisibleNodes(this.allNodes));
        this.nodeMap = CdwTreeComponent.buildNodeMap(newDataSource);

        // Apply any pending value that was set before nodeMap was ready
        if (this.pendingValue !== undefined) {
          this.writeValue(this.pendingValue);
        }
      }
    }

    // Handle filterValue changes
    if (has(changes, 'filterValue') && this.dataSource && this.allNodes) {
      if (this.filterValue) {
        this.filteredNodes = this.flattenTree(
          this.filterTree(this.dataSource, this.filterValue)
        );
        this.setRenderedNodes([...this.filteredNodes]);
      } else {
        this.allNodes.forEach((node) => {
          const nodeReference = node;
          if (nodeReference.isExpanded) {
            nodeReference.isExpanded = false;
          }
        });
        this.filteredNodes = [...this.allNodes];
        this.setRenderedNodes(this.getInitiallyVisibleNodes(this.allNodes));
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: unknown) => {
    // Placeholder for ControlValueAccessor - will be replaced by registerOnChange
  };

  onTouched = () => {
    // Placeholder for ControlValueAccessor - will be replaced by registerOnTouched
  };

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => fn();
  }

  writeValue(value: unknown) {
    if (!value) {
      this.selectedNodes = [];
      this.selectedNode = undefined;
      this.pendingValue = undefined;
      this.checkboxes?.forEach((checkbox) => checkbox.uncheck());
      return;
    }

    // If nodeMap isn't ready yet, store value and apply later
    if (!this.nodeMap || this.nodeMap.size === 0) {
      this.pendingValue = value;
      return;
    }

    // Clear pending value since we're applying now
    this.pendingValue = undefined;

    if (this.multiSelect && Array.isArray(value)) {
      value.forEach((nodeKey: string) => {
        const node = this.nodeMap.get(nodeKey);

        if (node) {
          node.selected = true;
        }

        if (!this.progressiveParentSelect) {
          const children = this.getNodeChildren(nodeKey);
          children.forEach((childKey) => {
            const child = this.nodeMap.get(childKey);
            if (child) {
              child.selected = true;
            }
          });
          this.updateParentNodeDeterminism(nodeKey);
        }
      });
    } else {
      this.selectedNode = this.allNodes?.find((node) => node.value === value);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  flattenTree(treeData: TreeNode[]): FlattenedTreeNode[] {
    const flattenedTree: FlattenedTreeNode[] = [];

    function flattenNode(node: TreeNode, level: number) {
      const { value, displayName, children, isExpanded } = node;
      const expandable = Array.isArray(children) && children.length > 0;

      flattenedTree.push({ value, displayName, level, expandable, isExpanded });

      if (expandable) {
        children.forEach((child: TreeNode) => flattenNode(child, level + 1));
      }
    }

    treeData.forEach((node: TreeNode) => flattenNode(node, 0));

    return flattenedTree;
  }

  getParentNode(node: FlattenedTreeNode) {
    const nodeIndex = this.renderedNodes?.findIndex(
      (nodeMatch) => nodeMatch.value === node.value
    );

    if (nodeIndex && this.renderedNodes) {
      for (let i = nodeIndex - 1; i >= 0; i--) {
        if (this.renderedNodes[i].level === node.level - 1) {
          return this.renderedNodes[i];
        }
      }
    }
    return null;
  }

  shouldRender(node: FlattenedTreeNode) {
    let parent = this.getParentNode(node);
    while (parent) {
      if (!parent.isExpanded) {
        return false;
      }
      parent = this.getParentNode(parent);
    }
    return true;
  }

  onRadioNodeClick(node: FlattenedTreeNode): void {
    if (!this.selectable || this.multiSelect) return;

    this.selectedNode = node;
    this.selectedNodeChanged.emit(this.selectedNode?.value as string);
    this.onChange(this.selectedNode?.value);
    this.onTouched();
  }

  onCheckboxChange(checkboxEvent: unknown) {
    const typedCheckboxEvent = checkboxEvent as {
      checkboxValue: FlattenedTreeNode;
      checked: boolean;
    };
    const { checkboxValue, checked } = typedCheckboxEvent;
    const nodeKey = checkboxValue.value as string;

    if (this.progressiveParentSelect) {
      const node = this.nodeMap.get(nodeKey);
      if (!node) return;

      const childKeys = this.getNodeChildren(nodeKey);

      if (childKeys.length > 0) {
        const allChildrenSelected = childKeys.every(
          (key) => this.nodeMap.get(key)?.selected
        );

        if (!(node.selected || allChildrenSelected)) {
          node.selected = true;
        } else if (node.selected && !allChildrenSelected) {
          node.selected = true;
          childKeys.forEach((key) => {
            const child = this.nodeMap.get(key);
            if (child) child.selected = true;
          });
        } else if (node.selected && allChildrenSelected) {
          node.selected = false;
        } else {
          node.selected = false;
          childKeys.forEach((key) => {
            const child = this.nodeMap.get(key);
            if (child) child.selected = false;
          });
        }
      } else {
        node.selected = checked;
      }

      this.syncProgressiveCheckboxes();
      this.emitSelectedNodes();
    } else if (this.multiSelect) {
      const node = this.nodeMap.get(nodeKey);
      if (!node) return;

      node.selected = checked;
      node.indeterminate = false;

      const childNodes = this.getNodeChildren(nodeKey);
      childNodes.forEach((childKey) => {
        const childNode = this.nodeMap.get(childKey);
        if (childNode) {
          childNode.selected = checked;
        }
      });

      this.updateParentNodeDeterminism(nodeKey);
      this.refreshOpenedCheckboxes();
      this.emitSelectedNodes();
    } else {
      if (checked) {
        this.selectedNode = checkboxValue;
        this.checkboxes.forEach((checkbox) => {
          const checkboxReference: CdwCheckboxComponent = checkbox;
          if (checkboxReference.value !== checkboxValue) {
            checkboxReference.checked = false;
            this.changeDetector.detectChanges();
          }
        });
      } else {
        this.selectedNode = undefined;
      }
      this.selectedNodeChanged.emit(this.selectedNode?.value as string);
      this.onChange(this.selectedNode?.value);
    }
    this.onTouched();
  }

  onNodeExpanded(node: FlattenedTreeNode) {
    const nodeReference = node;
    if (node.isExpanded) {
      nodeReference.isExpanded = false;
      // collapse child nodes on parent collapse
      if (this.renderedNodes) {
        const nodeIndex = this.renderedNodes?.findIndex(
          (nodeMatch) => nodeMatch.value === node.value
        );
        for (let i = nodeIndex + 1; i <= this.renderedNodes?.length; i++) {
          if (this.renderedNodes[i]?.level > node.level) {
            this.renderedNodes[i].isExpanded = false;
            this.renderedNodes.splice(i, 1);
            i--;
          } else {
            break;
          }
        }
        this.setRenderedNodes([...this.renderedNodes]);
      }
    } else {
      nodeReference.isExpanded = true;
      const nodeIndex = this.filteredNodes?.findIndex(
        (nodeMatch) => nodeReference.value === nodeMatch.value
      );
      const nodesToRender: FlattenedTreeNode[] = [];
      if (
        Array.isArray(this.filteredNodes) &&
        typeof nodeIndex === 'number' &&
        nodeIndex < this.filteredNodes.length
      ) {
        for (let i = nodeIndex + 1; i <= this.filteredNodes.length; i++) {
          if (this.filteredNodes[i]?.level === node.level + 1) {
            nodesToRender.push(this.filteredNodes[i]);
          } else if (this.filteredNodes[i]?.level === node.level) {
            break;
          }
        }
      }
      const flatIndex = this.renderedNodes?.findIndex(
        (nodeMatch) => nodeMatch.value === node.value
      );
      if (typeof flatIndex === 'number' && flatIndex >= 0) {
        this.renderedNodes?.splice(flatIndex + 1, 0, ...nodesToRender);
        if (this.renderedNodes?.length) {
          this.setRenderedNodes([...this.renderedNodes]);
        }
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  calculateNodePadding(node: FlattenedTreeNode) {
    const padding = node.level * 44;
    return `${padding}px`;
  }

  getNodeChecked(node: FlattenedTreeNode): boolean {
    const nodeValue = node.value as string;
    if (this.progressiveParentSelect) {
      const state = this.nodeMap.get(nodeValue);
      if (!state) return false;
      const childKeys = this.getNodeChildren(nodeValue);
      if (childKeys.length === 0) return state.selected;
      const allSelected = childKeys.every((k) => this.nodeMap.get(k)?.selected);
      return state.selected && allSelected;
    }
    if (this.multiSelect) {
      return !!this.nodeMap.get(nodeValue)?.selected;
    }
    return nodeValue === (this.selectedNode?.value as string);
  }

  getNodeIndeterminate(node: FlattenedTreeNode): boolean {
    const nodeValue = node.value as string;
    if (this.progressiveParentSelect) {
      const state = this.nodeMap.get(nodeValue);
      if (!state) return false;
      const childKeys = this.getNodeChildren(nodeValue);
      if (childKeys.length === 0) return false;
      const allSelected = childKeys.every((k) => this.nodeMap.get(k)?.selected);
      if (state.selected && allSelected) return false;
      const someSelected = childKeys.some((k) => this.nodeMap.get(k)?.selected);
      return state.selected || someSelected;
    }
    if (this.multiSelect) {
      return !!this.nodeMap.get(nodeValue)?.indeterminate;
    }
    return false;
  }

  isBackgroundIndeterminate(node: FlattenedTreeNode): boolean {
    if (!this.progressiveParentSelect) return false;
    return this.getNodeIndeterminate(node);
  }

  isFilledIndeterminate(node: FlattenedTreeNode): boolean {
    if (!this.progressiveParentSelect) return false;
    const nodeValue = node.value as string;
    const state = this.nodeMap.get(nodeValue);
    if (!state?.selected) return false;
    const childKeys = this.getNodeChildren(nodeValue);
    if (childKeys.length === 0) return false;
    const allSelected = childKeys.every((k) => this.nodeMap.get(k)?.selected);
    return !allSelected;
  }

  filterTree(tree: TreeNode[], filterText: string): TreeNode[] {
    function findParent(
      nodes: TreeNode[],
      child: TreeNode
    ): TreeNode | undefined {
      for (const node of nodes) {
        if (node.children?.includes(child)) {
          return node;
        }
        const parent = findParent(node.children || [], child);
        if (parent) return parent;
      }
      return undefined;
    }

    function expandAncestors(node: TreeNode | undefined): void {
      if (!node) return;

      const nodeReference = node;
      if (nodeReference?.isExpanded !== true) {
        nodeReference.isExpanded = true;
      }

      expandAncestors(findParent(tree, node));
    }

    const filteredNodes: TreeNode[] = tree
      .map((node) => {
        if (!filterText) {
          return {
            ...node,
            children: node.children
              ? this.filterTree(node.children, filterText)
              : undefined,
          };
        }
        if (
          node.displayName.toLowerCase().includes(filterText.toLowerCase()) ||
          (typeof node.value === 'string' &&
            node.value.toLowerCase().includes(filterText.toLowerCase()))
        ) {
          expandAncestors(node);
          return {
            ...node,
            children: node.children
              ? this.filterTree(node.children, filterText)
              : undefined,
          };
        }
        if (node.children) {
          const filteredChildren = this.filterTree(node.children, filterText);
          if (filteredChildren.length > 0) {
            return { ...node, isExpanded: true, children: filteredChildren };
          }
        }
        return undefined;
      })
      .filter((node) => node !== undefined) as TreeNode[];
    return filteredNodes;
  }

  private getInitiallyVisibleNodes(
    allNodes: FlattenedTreeNode[]
  ): FlattenedTreeNode[] {
    if (!allNodes.some((n) => n.isExpanded)) {
      return allNodes.filter((n) => n.level === 0);
    }

    const visible: FlattenedTreeNode[] = [];
    const expandedAtLevel: boolean[] = [];

    for (const node of allNodes) {
      if (node.level === 0) {
        visible.push(node);
        expandedAtLevel[0] = !!node.isExpanded;
      } else {
        let ancestorsExpanded = true;
        for (let l = 0; l < node.level; l++) {
          if (!expandedAtLevel[l]) {
            ancestorsExpanded = false;
            break;
          }
        }
        if (ancestorsExpanded) {
          visible.push(node);
          expandedAtLevel[node.level] = !!node.isExpanded;
        }
      }
    }
    return visible;
  }

  private setRenderedNodes(nodes: FlattenedTreeNode[]): void {
    this.renderedNodes = nodes;
    this.renderedNodeCountChanged.emit(nodes.length);

    // CDK virtual scroll measures viewport size on init but doesn't re-measure
    // when the host element's height changes dynamically. Force a re-check so
    // all items within the updated viewport are rendered.
    setTimeout(() => this.viewport?.checkViewportSize());
  }

  private getNodeChildren(nodeKey: string): string[] {
    const nodeKeyStack: string[] = [nodeKey];
    const descendants: string[] = [];

    while (nodeKeyStack.length > 0) {
      const currentKey = nodeKeyStack.pop();
      if (!currentKey) break;

      const currentNode = this.nodeMap.get(currentKey);
      if (currentNode?.children && currentNode.children.length > 0) {
        currentNode.children.forEach((child) => {
          const childValue = child.value as string;
          nodeKeyStack.push(childValue);
          descendants.push(childValue);
        });
      }
    }

    return descendants;
  }

  private refreshOpenedCheckboxes() {
    this.checkboxes.forEach((checkbox) => {
      const checkboxValue = (checkbox?.value as FlattenedTreeNode)
        .value as string;
      const checkboxReference = checkbox;
      if (this.multiSelect) {
        const nodeMapNode = this.nodeMap.get(checkboxValue);
        checkboxReference.checked = !!nodeMapNode?.selected;
        checkboxReference.indeterminate = !!nodeMapNode?.indeterminate;
        this.changeDetector.detectChanges();
      } else {
        checkboxReference.checked = checkboxValue === this.selectedNode?.value;
      }
    });
    this.changeDetector.detectChanges();
  }

  private syncProgressiveCheckboxes(): void {
    this.checkboxes.forEach((cb) => {
      const node = cb.value as FlattenedTreeNode;
      const shouldBeChecked = this.getNodeChecked(node);
      const shouldBeIndeterminate = this.getNodeIndeterminate(node);

      cb.indeterminate = shouldBeIndeterminate;
      if (shouldBeChecked) {
        cb.check();
      } else {
        cb.uncheck();
      }
    });
  }

  private updateParentNodeDeterminism(nodeKey: string) {
    let currentNode = this.nodeMap.get(nodeKey);

    while (currentNode?.parent) {
      const currentNodeParentValue = currentNode.parent.value as string;
      const parent = this.nodeMap.get(currentNodeParentValue);

      if (!parent) break;

      const allChildrenSelected = parent.children?.every(
        (child) => this.nodeMap.get(child.value as string)?.selected
      );

      const someChildrenSelected = parent.children?.some(
        (child) =>
          this.nodeMap.get(child.value as string)?.selected ||
          this.nodeMap.get(child.value as string)?.indeterminate
      );

      if (allChildrenSelected) {
        parent.selected = true;
        parent.indeterminate = false;
      } else if (someChildrenSelected) {
        parent.selected = false;
        parent.indeterminate = true;
      } else {
        parent.selected = false;
        parent.indeterminate = false;
      }

      currentNode = parent;
    }
  }

  private emitSelectedNodes(): void {
    const selectedNodes: string[] = [];

    this.nodeMap.forEach((node, nodeKey) => {
      if (!node.selected) return;
      if (!this.progressiveParentSelect && node.children?.length) return;
      if (!selectedNodes.includes(nodeKey as string)) {
        selectedNodes.push(nodeKey as string);
      }
    });

    this.selectedNodeChanged.emit(selectedNodes);
    this.onChange(selectedNodes);
  }
}
